import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signIn, withTimeout } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../components/Logo';
import { pocetnaStranica } from '../components/PrivateRoute';
import { VERZIJA_DATUM } from '../verzija';

const EMAIL_KLJUC = 'triglav_zadnji_email';

const zapamcenEmail = (): string => {
  try { return localStorage.getItem(EMAIL_KLJUC) ?? ''; } catch { return ''; }
};

const Login: React.FC = () => {
  const [email, setEmail] = useState(zapamcenEmail);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { user, profile, profileMissing } = useAuth();
  const navigate = useNavigate();

  // Ako je već ulogovan, preusmjeri na odgovarajući dashboard
  useEffect(() => {
    if (user && profile) {
      // Prva stranica koju korisnik smije vidjeti — ovisi o dozvolama, ne o ulozi.
      navigate(pocetnaStranica(profile), { replace: true });
    }
  }, [user, profile, navigate]);

  // Sesija je uspješno otvorena, ali profil se ne može učitati — nema smisla
  // ostaviti dugme da vrti zauvijek bez objašnjenja.
  useEffect(() => {
    if (user && profileMissing) {
      setLoading(false);
      setError('Nalog je prijavljen, ali profil korisnika nije pronađen. Obratite se administratoru.');
    }
  }, [user, profileMissing]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { error: authError } = await withTimeout(signIn(email, password), 15000, 'signIn');

      if (authError) {
        setError('Pogrešan email ili lozinka.');
        setLoading(false);
        return;
      }
      try { localStorage.setItem(EMAIL_KLJUC, email); } catch { /* privatni prozor */ }
      // AuthContext će reagovati na onAuthStateChange i useEffect gore preusmjerava
    } catch (err) {
      console.error('signIn:', err);
      setError('Prijava traje duže nego očekivano. Provjerite internet i pokušajte ponovo.');
      setLoading(false);
    }
  };

  return (
    <div style={styles.wrapper}>
      <div style={styles.card}>
        <Logo variant="login" />
        <p style={styles.subtitle}>Računarko</p>

        <form onSubmit={handleSubmit} style={styles.form}>
          <label style={styles.label}>
            Email
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              autoFocus={!email}
              autoComplete="username"
              style={styles.input}
            />
          </label>

          <label style={styles.label}>
            Lozinka
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              autoFocus={!!email}
              autoComplete="current-password"
              style={styles.input}
            />
          </label>

          {error && <p style={styles.error}>{error}</p>}

          <button type="submit" disabled={loading} style={styles.button}>
            {loading ? 'Prijavljivanje...' : 'Prijavi se'}
          </button>
        </form>

        <p style={styles.verzija}>Verzija od {VERZIJA_DATUM}</p>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  verzija: {
    margin: '1.25rem 0 0', textAlign: 'center',
    fontSize: '0.72rem', color: '#94a3b8',
  },
  wrapper: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f5f5',
    padding: '1rem',
    boxSizing: 'border-box',
  },
  card: {
    backgroundColor: '#fff',
    padding: 'clamp(1.5rem, 6vw, 2.5rem)',
    borderRadius: '8px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
    width: '100%',
    maxWidth: '380px',
  },
  title: {
    margin: '0 0 0.25rem',
    fontSize: '1.4rem',
    fontWeight: 700,
    color: '#1a1a1a',
  },
  subtitle: {
    margin: '0 0 2rem',
    color: '#666',
    fontSize: '0.9rem',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.2rem',
  },
  label: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#333',
  },
  input: {
    padding: '0.6rem 0.75rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '1rem',
    outline: 'none',
  },
  error: {
    margin: 0,
    padding: '0.6rem 0.75rem',
    backgroundColor: '#fef2f2',
    border: '1px solid #fca5a5',
    borderRadius: '4px',
    color: '#dc2626',
    fontSize: '0.875rem',
  },
  button: {
    padding: '0.7rem',
    backgroundColor: '#2563eb',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: '0.5rem',
  },
};

export default Login;
